import React, { useEffect, useState, useCallback } from 'react';
import SectionHeader from '../components/SectionHeader';
import NewsletterOption from '../../newsletter/components/NewsletterOption';
import { List } from '../../newsletter/types/Newsletter';
import { subscribeToNewsletter, unsubscribeFromNewsletter } from '../../newsletter/services/NewsletterService';
import { fetchSubscribedNewsletters } from '../../newsletter-landing/services/NewsletterPageService';
import { SectionData } from '../types/types';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useTranslations } from '../../localization/useTranslations';
import AccountBar from '../../components/account-bar/AccountBar';
import { usePremiumStatus } from '../../newsletter-landing/helpers/CheckPremiumStatus';
import useScrollbackAfterRedirect from '../../hooks/useScrollbackAfterRedirect';
import { handlePendingAction } from '../../newsletter-landing/helpers/handlePendingAction';

interface NewsletterBoxProps {
  hasBackground?: boolean | 1 | ''; // from 'data-has-background' boolean
  sectionDatas?: SectionData;
  selectedNewsletters: string[]; // Array of selected newsletter codes
  isInOnboarding?: boolean;
  isOnThankyouPage?: boolean;
}

const NewsletterBox: React.FC<NewsletterBoxProps> = ({
  hasBackground,
  sectionDatas,
  selectedNewsletters,
  isInOnboarding,
  isOnThankyouPage,
}) => {
  useScrollbackAfterRedirect(); // Restore scroll position if redirected from login

  const [subscribedNewsletters, setSubscribedNewsletters] = useState<string[]>([]);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const translations = useTranslations();
  const [shouldOpenPremodal, setShouldOpenPremodal] = useState(false);
  const { isUserPremium, isPremiumStateLoading } = usePremiumStatus();
  const [isLoading, setIsLoading] = useState(false);

  // Check if the section has an external link icon
  const isExternal = sectionDatas?.linkIcon === 'external';

  // Function to open the snackbar with a specific message
  const openSnackbar = useCallback((message: string) => {
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  }, []);

  useEffect(() => {
    if (window.crm_user) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }

    const fetchSubscribedCodes = async () => {
      try {
        const subscribedCodes = await fetchSubscribedNewsletters();
        setSubscribedNewsletters(subscribedCodes);
      } catch (error) {
        console.error(error);
        openSnackbar(
          translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again later.'
        );
      }
    };

    fetchSubscribedCodes();
  }, [openSnackbar, translations?.snackBarMessages?.somethingWentWrong]);

  /** Check if a newsletter is subscribed by the user */
  const checkIsSubscribed = (newsletter: List): boolean => {
    return subscribedNewsletters.includes(newsletter.code);
  };

  /** Handle subscribe/unsubscribe toggle for newsletters */
  const handleToggleChange = async (newsletter: List) => {
    try {
      if (checkIsSubscribed(newsletter)) {
        await unsubscribeFromNewsletter(newsletter.code);
        setSubscribedNewsletters((prev) => prev.filter((code) => code !== newsletter.code));
      } else {
        await subscribeToNewsletter(newsletter.code);
        setSubscribedNewsletters((prev) => [...prev, newsletter.code]);
      }
      openSnackbar(
        checkIsSubscribed(newsletter)
          ? translations?.snackBarMessages?.unsubscribedFromNewsletter
          : translations?.snackBarMessages?.subscribedToNewsletter
      );
    } catch (error) {
      console.error(error);
      openSnackbar(translations?.snackBarMessages?.somethingWentWrong);
    } finally {
      setIsLoading(false);
    }
  };

  // Close the snackbar
  const onCloseSnackbar = () => {
    setIsSnackbarOpen(false);
  };

  useEffect(() => {
    if (isPremiumStateLoading) return;

    handlePendingAction(
      isUserPremium ?? false,
      openSnackbar,
      setSubscribedNewsletters,
      setShouldOpenPremodal,
      translations
    );
  }, [isPremiumStateLoading, isUserPremium, openSnackbar, translations]);

  // Determine background class based on the content type
  const bgClassModifier =
    window.contentType === 'life' ? 'react-newsletter-block--life-bg' : 'react-newsletter-block--forbes-bg';

  // Filter the newsletters to only include the selected ones
  const displayedNewsletters =
    window.NewsletterLists?.lists
      ?.filter((newsletter) => selectedNewsletters?.includes(newsletter.code)) // Filter by selected codes
      .sort((a, b) => {
        // Sort by the order in selectedNewsletters to maintain order as selected in wp-admin
        return selectedNewsletters.indexOf(a.code) - selectedNewsletters.indexOf(b.code);
      }) || [];

  // In onboarding, we just want to render the list of NLs
  if (isInOnboarding) {
    const onboardingNewsletterList = displayedNewsletters.filter(
      (newsletter) => newsletter.code !== 'newsletter-offers' // Remove the newsletter-offers from the onboarding modal - auto sub on request (CZ)
    );

    return (
      <>
        {onboardingNewsletterList.map((newsletter) => (
          <>
            <CustomSnackbar message={snackbarMessage} open={isSnackbarOpen} onClose={onCloseSnackbar} />
            <NewsletterOption
              key={newsletter.code}
              title={newsletter.title}
              description={newsletter.description}
              code={newsletter.code}
              isSubscribed={checkIsSubscribed(newsletter)}
              newsletter={newsletter}
              onChange={handleToggleChange}
              shouldOpenPremodal={shouldOpenPremodal}
              isUserPremium={isUserPremium}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          </>
        ))}
      </>
    );
  }

  if (isOnThankyouPage) {
    const thankyouNewsletters = displayedNewsletters.filter((newsletter) => {
      if (window.country.toUpperCase() === 'CZ') {
        return newsletter.code === 'newsletter-espresso';
      } else if (window.country.toUpperCase() === 'SK') {
        return newsletter.code === 'newsletter-forbes-digest';
      }

      return null;
    });

    return thankyouNewsletters.length > 0 ? (
      <>
        <CustomSnackbar message={snackbarMessage} open={isSnackbarOpen} onClose={onCloseSnackbar} />
        <NewsletterOption
          title={thankyouNewsletters[0].title}
          description={thankyouNewsletters[0].description}
          code={thankyouNewsletters[0].code}
          newsletter={thankyouNewsletters[0]}
          onChange={handleToggleChange}
          isUserPremium={isUserPremium}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isSubscribed={checkIsSubscribed(thankyouNewsletters[0])}
        />
      </>
    ) : null;
  }

  return (
    <>
      <CustomSnackbar message={snackbarMessage} open={isSnackbarOpen} onClose={onCloseSnackbar} />

      <div className={`react-newsletter-block ${hasBackground ? bgClassModifier : ''}`}>
        <div className="react-newsletter-block__box">
          {sectionDatas && (
            <SectionHeader
              title={sectionDatas.title}
              linkLabel={sectionDatas.sectionLinkText}
              url={sectionDatas.sectionUrl}
              hasPreIcon={sectionDatas.showIcon}
              isExternal={isExternal}
              iconUrl={sectionDatas.iconUrl}
              hasTitleLink={sectionDatas.hasTitleLink}
              hideHeader={sectionDatas.hideHeader}
            />
          )}

          {isLoggedIn ? (
            <AccountBar />
          ) : (
            <p className="react-newsletter-block__information basic-16">
              {translations?.newsletter?.newsletterAdditionalMessage ||
                'To subscribe to the newsletter you must be registered and logged in to Forbes'}
            </p>
          )}

          {/* Render available newsletters that are selected */}
          {displayedNewsletters.map((newsletter) => (
            <NewsletterOption
              key={newsletter.code}
              title={newsletter.title}
              description={newsletter.description}
              code={newsletter.code}
              isSubscribed={checkIsSubscribed(newsletter)}
              newsletter={newsletter}
              onChange={handleToggleChange}
              shouldOpenPremodal={shouldOpenPremodal}
              isUserPremium={isUserPremium}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default NewsletterBox;
